<template>
  <div class="search-float-root">
    <!-- 右下角悬浮搜索按钮（弹窗关闭时显示）：PC 与移动端各自适配 -->
    <div v-if="!open" class="btn-switch" :class="{ 'loaded': btnLoaded }">
      <!-- PC 端悬浮按钮（ai.png）+ 底部“中峪AI助手”名牌 -->
      <div v-if="!isMobile" class="float-holder">
        <div class="float-btn" @click="openWidget" title="中峪AI助手">
          <span class="float-tag">中峪AI助手</span>
        </div>
      </div>
      <!-- 移动端右下角 AI 机器人图标（JS 动态补偿，屏幕上呈现物理尺寸） -->
      <div
        v-else
        class="float-btn-mobile"
        :style="mobileBtnStyle"
        @click="openWidget"
        title="中峪AI助手"
      >
        <div class="float-btn-pulse">
          <span class="float-tag-mobile" :style="mobileTagStyle">中峪AI助手</span>
        </div>
      </div>
    </div>

    <!-- 手机端弹窗的黑色蒙层（点击收起），底层铺满布局视口 -->
    <div
      v-if="open && isMobile"
      class="float-mask"
      @touchstart.prevent.stop="closeWidget"
      @click="closeWidget"
    ></div>

    <!-- 右下角搜索弹窗（对话框 / 聊天形式），支持右下角拖拽拉伸（有最小尺寸） -->
    <div
      v-if="open"
      class="float-dialog"
      :class="{ 'is-mobile': isMobile, 'is-ios': isIos }"
      :style="dialogStyle"
    >
      <!-- 顶部上下拉伸把手（仅调整高度，锚定底边不动） -->
      <div
        v-if="!isMobile"
        class="resize-handle-top"
        @mousedown.prevent="startResizeTop"
        title="拖拽调整高度"
      ></div>
      <!-- 左侧左右拉伸把手（仅调整宽度，锚定右边缘不动） -->
      <div
        v-if="!isMobile"
        class="resize-handle-left"
        @mousedown.prevent="startResizeLeft"
        title="拖拽调整宽度"
      ></div>

      <!-- 头部 -->
      <div class="dialog-header">
        <div class="dialog-title">
          <span class="dialog-dot"></span>
          中峪AI助手
        </div>
        <div class="dialog-actions">
          <button class="mini-btn" @click="clearChat" title="清空会话">清空</button>
          <button class="close-btn" @touchstart.prevent.stop="closeWidget" @click="closeWidget" title="收起">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:16px;height:16px">
              <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- 消息区 -->
      <div class="dialog-body" ref="bodyRef">
        <!-- 空状态：快捷问题 -->
        <div v-if="messages.length === 0" class="welcome">
          <div class="welcome-title">您好，我是中峪AI助手</div>
          <div class="welcome-sub">基于公开资料智能问答，点击下方问题或直接输入开始探索</div>
          <div class="quick-list">
            <div
              v-for="(w, i) in hotWords"
              :key="i"
              class="quick-item"
              @click="quickSearch(w)"
            >{{ w }}</div>
          </div>
        </div>

        <!-- 对话气泡 -->
        <div
          v-for="(msg, index) in messages"
          :key="index"
          class="chat-row"
          :class="msg.role === 'user' ? 'row-user' : 'row-ai'"
        >
          <div class="chat-bubble" :class="msg.role === 'user' ? 'bubble-user' : 'bubble-ai'">
            <template v-if="msg.role === 'user'">
              {{ msg.text }}
            </template>
            <template v-else>
              <!-- AI 加载中 -->
              <div v-if="msg.loading" class="loading-row">
                <span class="dot"></span><span class="dot"></span><span class="dot"></span>
                <span class="loading-text">正在思考...</span>
              </div>
              <!-- AI 错误 -->
              <div
                v-else-if="msg.error"
                class="ai-error"
                :class="{ 'ai-error-normal': msg.errorCode === 'NETWORK_ERROR' }"
              >{{ msg.text }}</div>
              <!-- AI 正文（打字机） -->
              <div v-else class="ai-answer" v-html="msg.displayedHtml"></div>
              <!-- 参考资料 -->
              <div v-if="!msg.loading && !msg.error && msg.referenceGroups && msg.referenceGroups.length" class="refs">
                <div class="refs-title">参考资料 ({{ refCount(msg.referenceGroups) }})</div>
                <template v-for="(group, gi) in msg.referenceGroups">
                  <!-- primary=false 为农业官网资料，前置分组标题 -->
                  <div v-if="!group.primary" :key="'g'+gi" class="refs-group-title">
                    以下参考资料来自{{ group.source || '农业官网' }}
                  </div>
                  <div
                    v-for="(ref, ri) in group.references"
                    :key="'r'+gi+'-'+ri"
                    class="ref-item"
                    :class="{ 'ref-item-agri': group.sourceKey === 'zoneyucc' }"
                    @click="openRef(ref.url)"
                  >
                    <img
                      v-if="ref.coverUrl"
                      class="ref-cover"
                      :src="ref.coverUrl"
                      alt=""
                      loading="lazy"
                    />
                    <div class="ref-main">
                      <span class="ref-title" v-html="highlightKeyword(ref.title, msg.keyword)"></span>
                      <span class="ref-src" v-if="ref.source">{{ ref.source }}</span>
                      <span class="ref-summary" v-if="ref.summary">{{ ref.summary }}</span>
                    </div>
                  </div>
                </template>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- 右下角“滚动到底部”向下箭头（非底部时显示，点击回到底部）。
           移到滚动容器 .dialog-body 之外、相对整窗 .float-dialog 绝对定位，
           既能在可视区右下角固定不动，隐藏时又不影响 .dialog-body 的滚动布局 -->
      <transition name="fade">
        <div
          v-if="showScrollDown && !isMobile"
          class="scroll-down-btn"
          @click="scrollToBottomSmooth"
          title="滚动到底部"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 9l7 7 7-7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </transition>

      <!-- 输入区 -->
      <div class="dialog-footer">
        <!-- 联想下拉框（精确贴住输入框上方，随输入框容器定位，避免被弹窗裁剪） -->
        <div v-if="showSuggestions" class="suggest-box">
          <div
            v-for="(sug, i) in suggestions"
            :key="i"
            class="suggest-item"
            :class="{ 'suggest-active': i === activeSuggestionIndex }"
            @mousedown.prevent="selectSuggestion(sug)"
            @mouseenter="activeSuggestionIndex = i"
          >
            <span class="suggest-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:15px;height:15px">
                <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <span class="suggest-text" v-html="highlightKeyword(sug.question, keyword)"></span>
          </div>
        </div>

        <textarea
          ref="chatInput"
          v-model="keyword"
          class="chat-input"
          rows="1"
          placeholder="请输入问题，按 Enter 发送..."
          @input="onInput"
          @focus="onInputFocus"
          @blur="onInputBlur"
          @keydown.enter.exact.prevent="onEnterKey"
          @keydown.up.prevent="onSuggestionKeydown(-1)"
          @keydown.down.prevent="onSuggestionKeydown(1)"
        ></textarea>
        <button class="send-btn" @click="sendMessage" :disabled="isSearching">
          <svg v-if="!isSearching" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:20px;height:20px">
            <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span v-else class="send-loading"></span>
        </button>
      </div>
      <!-- 右下角拉伸把手 -->
      <div class="resize-handle" @mousedown.prevent="startResize" title="拖拽调整大小"></div>
    </div>
  </div>
</template>

<script>
import { publicSearch, publicSearchSuggestions } from "@/api/search.js";

// 接口错误码 → 用户可读提示
const ERROR_MESSAGES = {
  PUBLIC_SEARCH_API_KEY_MISSING: "搜索服务未配置密钥，请联系管理员。",
  PUBLIC_SEARCH_API_KEY_INVALID: "搜索服务密钥无效，请联系管理员。",
  PUBLIC_SEARCH_SITE_DISABLED: "当前官网搜索服务未启用。",
  VALIDATION_ERROR: "请输入有效的问题。",
  MODEL_REQUEST_FAILED: "回答生成失败，请稍后重试。",
  NETWORK_ERROR: "网络开小差啦！请重新发送。",
};

export default {
  name: "SearchFloat",
  data() {
    return {
      open: false,
      isMobile: false,
      isIos: false,
      // 页面加载后置位，用于触发浮窗从右下角滑入到位的入场动画
      btnLoaded: false,
      keyword: "",
      hotWords: ["中峪集团", "道地药材", "乡村振兴", "集团产业", "项目工程", "基地规模"],
      messages: [],
      isSearching: false,
      // 联想相关
      suggestions: [],
      showSuggestions: false,
      activeSuggestionIndex: -1,
      suggestionDebounceTimer: null,
      suggestionController: null,
      suggestionRequestId: 0,
      suggestionInputFocused: false,
      // 弹窗尺寸（默认 980x620，支持右下角拖拽拉伸）
      dialogW: 980,
      dialogH: 620,
      dialogMinW: 420,
      dialogMinH: 400,
      resizing: false,
      // 消息区是否滚动到了底部（非底部时显示右下角向下箭头）
      showScrollDown: false,
      // 递增式搜索序号：清空会话时自增，使在途请求结果作废
      searchSeq: 0,
      // 页面整体缩放系数（viewport meta / #app transform 统一为 screen.width/1600）。
      // 手机端浮窗需除以该系数补偿，才能在屏幕上呈现合理的物理尺寸。
      pageScale: 1,
      // 手机端输入框是否聚焦（即软键盘是否弹出）
      kbActive: false,
      // 软键盘弹出时的可视区高度（布局 px），用于把弹窗收缩并定位到键盘上方
      kbVisibleH: 0,
      // 软键盘弹出时可视区顶部的偏移（布局 px）：iOS 滚动可视视口时可能非 0
      kbVisTop: 0,
      // 手机端弹窗的实测净缩放（渲染尺寸 / 本地 style 尺寸）；0 表示尚未测量
      mobileNetScale: 0,
    };
  },
  computed: {
    // 手机端按钮：期望物理尺寸 56×56，距右 16、距底 24，除以缩放系数还原为 CSS 像素，
    // 使 meta 缩放后屏幕上显示为目标物理尺寸。
    mobileBtnStyle() {
      const s = this.pageScale || 1;
      return {
        right: 16 / s + "px",
        bottom: 24 / s + "px",
        width: 56 / s + "px",
        height: 56 / s + "px",
      };
    },
    // 手机端按钮名牌“中峪AI助手”：与按钮、AI 机器人图片同除以 pageScale 补偿，
    // 使名牌在 meta 缩放后同样放大为正视的物理尺寸（否则名牌不会随机器人放大）。
    mobileTagStyle() {
      const s = this.pageScale || 1;
      return {
        // 名牌底边与 AI 机器人底边重叠（bottom:0 贴合按钮底边）
        bottom: 0,
        padding: `${2 / s}px ${6 / s}px`,
        fontSize: 9 / s + "px",
        borderRadius: 5 / s + "px",
      };
    },
    // 弹窗样式：PC 用手动拖拽宽高；手机端宽高取屏幕比例居中。
    // 安卓走 zoom(9) + scale(1/3) 高清方案（净视觉 = 原 scale(3) 且不发虚）；
    // iOS 上 zoom 行为异常（字体不放大而框放大），故改用整体 scale 等比放大。
    // 键盘弹出时收缩弹窗高度并把整窗下移到「键盘上方可视区」内，避免输入框被遮挡。
    dialogStyle() {
      if (!this.isMobile) {
        // 默认尺寸偏大，窗口较小时按视口留边收敛，避免弹窗超出屏幕
        return {
          width: Math.min(this.dialogW, window.innerWidth - 48) + "px",
          height: Math.min(this.dialogH, window.innerHeight - 48) + "px",
        };
      }
      // 弹窗是 position:fixed，根节点已挂到 body 下（见 mounted），包含块是「布局视口」，
      // 在 width=1600 方案下固定为 1600 CSS px，而不是设备物理像素（window.screen.width 只有 375）。
      // 若按 screen 算，再乘净放大 3 倍，弹窗只占布局视口的 375×0.96×3/1600 ≈ 67%，屏幕上就显得偏小、铺不满。
      // 因此基准尺寸必须按布局视口（documentElement.clientWidth/clientHeight）计算，
      // 再除以净放大倍数 3（iOS/安卓最终都是 9×(1/3)），屏幕上才呈现 85% 的观感。
      const layoutW = document.documentElement.clientWidth || window.innerWidth;
      const layoutH = document.documentElement.clientHeight || window.innerHeight;
      const net = 3;
      const w = Math.round((layoutW * 0.85) / net);
      // 弹窗高度取布局视口的 0.85：不铺满，四周各留约 7.5% 空白，确保输入框可见
      const h = Math.round((layoutH * 0.85) / net);
      // 手机端“字体放大一号”：内部排版倍数（iOS 用容器 scale、安卓用 zoom）统一 ×F，
      // 同时把容器基准尺寸 ÷F。这样弹窗外框的视觉尺寸不变，只有内部文字/内边距整体放大约 15%。
      const F = 1.15;
      const isIos = this.isIos;
      // 我方设置的 transform scale：iOS 以 ×9 设计稿排版后缩回 1/3，安卓以 zoom(9F) 后缩回 1/3
      const transformScale = isIos ? 0.3333 * F : 0.3333;
      // 实测净缩放（渲染尺寸 / style 尺寸）：安卓 = zoom(9F)×1/3 = 3F，iOS = 1×transformScale。
      // 未测到时按几何常量兜底，避免脏值把弹窗算飞。
      const netScale = this.mobileNetScale > 0
        ? this.mobileNetScale
        : (isIos ? transformScale : 3 * F);
      const styleW = isIos ? (w * 9) / F : w / F;
      // 未受键盘影响时弹窗的本地高度（iOS 沿用既有的 6.9 比例，略矮于安卓）
      const baseStyleH = isIos ? (h * 6.9) / F : h / F;

      // —— 软键盘 ——
      // 键盘弹出后可视区只剩一小条（约屏高 55%），而弹窗本身占屏高 65%~85%，整窗必然放不下。
      // 故键盘弹出时把「渲染高度」收缩到可视高度的 92%（渲染高度 = styleH × netScale），
      // 再把整窗下移，使其底边刚好落在键盘上方 —— 上下都在可视区内，整窗可见。
      // 说明：Android Chrome 默认 interactive-widget=resizes-content，键盘弹出会同时缩小布局视口，
      // 此时 layoutH 与可视高度同步变小，弹窗自然缩小居中（kbUp 为 false），无需额外处理。
      const vvH = this.kbVisibleH || layoutH;
      const kbUp = this.kbActive && vvH > 0 && vvH < layoutH - 1;
      const visH = kbUp ? vvH : layoutH;
      const kbMargin = 48; // 布局 px（≈11 物理 px），弹窗底边与键盘之间的留白
      const styleH = kbUp
        ? Math.min(baseStyleH, (visH * 0.92) / netScale)
        : baseStyleH;

      // —— 垂直定位 ——
      // 用 top 百分比定位，而不是 translate 的 px 位移：百分比在包含块（布局视口）坐标系里解析，
      // 与弹窗自身的 zoom/scale 无关；而 translate 的 px 位移会被 zoom 放大（实测放大倍数就是 zoom：
      // 安卓 10.35×、iOS 1×）。此前按「视觉位移 ÷ 净缩放」换算，安卓/iOS 都会把弹窗顶出屏幕，
      // 这正是“输入框聚焦后看不到整个弹窗”的根因。
      // 实测：top:X% 配合 translate(-50%,-50%) 时，弹窗渲染后的中心恰好落在 X% × 布局高 处。
      const renderedH = styleH * netScale;
      const centerY = kbUp
        ? (this.kbVisTop || 0) + visH - kbMargin - renderedH / 2 // 底边贴住键盘上方，整窗可见
        : layoutH / 2;                                           // 未弹键盘：垂直居中
      return {
        left: "50%",
        top: ((centerY / layoutH) * 100).toFixed(4) + "%",
        width: styleW + "px",
        height: styleH + "px",
        // 水平、垂直居中仍由 translate(-50%,-50%) 完成；垂直位置由上面的 top 精确决定
        transform: `translate(-50%, -50%) scale(${transformScale.toFixed(4)})`,
        transformOrigin: "center",
      };
    },
  },
  watch: {
    // 输入框内容被清空（发送后 / 手动删空 / 清空会话）时，重置 iOS 的占位文案排版。
    // 详见 iosResetPlaceholder 的说明。
    keyword(v) {
      if (!v) this.iosResetPlaceholder();
    },
    // 对话消息 / 弹窗尺寸变化后，消息区可能从不可滚动变为可滚动（或反之），
    // 需要主动刷新一次“是否在底部”，从而正确显示/隐藏右下角向下箭头。
    messages(v) {
      if (!this.open) return;
      this.$nextTick(() => {
        if (v.length) this.onBodyScroll();
      });
    },
    dialogW() {
      if (!this.open) return;
      this.$nextTick(() => this.onBodyScroll());
    },
    dialogH() {
      if (!this.open) return;
      this.$nextTick(() => this.onBodyScroll());
    },
    // 弹窗打开后 bodyRef 才渲染，此时绑定 scroll 监听并刷新一次箭头状态
    open(v) {
      if (v) {
        this.$nextTick(() => {
          const bodyEl = this.$refs.bodyRef;
          if (bodyEl) {
            bodyEl.addEventListener("scroll", this._onBodyScroll, { passive: true });
          }
          this.onBodyScroll();
        });
      }
    },
  },
  mounted() {
    // 与 App（app.vue L662-663）一致地按 UA 判断是否手机
    const mobileKeywords = /Mobile|Android|iPhone|iPad|iPod|Windows Phone/i;
    this.isMobile = mobileKeywords.test(navigator.userAgent);
    // iOS：Safari/Chrome iOS 上 zoom 行为异常（字体不放大而框放大），需区分安卓/iOS 处理
    this.isIos = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    // 读取页面整体缩放系数（viewport meta 的 initial-scale 即 screen.width/1600），
    // 手机端用于对浮窗尺寸做反向补偿，使 UI 呈现为合理的物理尺寸。
    if (!this.syncPageScale()) {
      this.pageScale = window.screen.width / 1600;
    }

    // 阻止滚轮/触摸滚动穿透到背景页面（挂在根节点，捕获阶段拦截 default 滚动）
    this._wheelBlocker = this.onWheelBlocker.bind(this);
    this.$el.addEventListener("wheel", this._wheelBlocker, { passive: false });
    this.$el.addEventListener("touchmove", this._wheelBlocker, { passive: false });

    // 消息区滚动监听：弹窗默认关闭、bodyRef 尚未渲染，改在 open 变为 true 时绑定
    this._userMovedScroll = false; // 用户是否已手动滚动过一次（跟随停止开关）
    this._lastAuto = 0; // 最近一次程序滚动的打点
    this._onBodyScroll = () => {
      // 程序滚动刚发生（抑制窗口内）不算用户滚动；否则视为用户手动滚动
      if (Date.now() - this._lastAuto > 220) this._userMovedScroll = true;
      this.onBodyScroll();
    };

    // 参考 header 的处理方式：等初始渲染/交互稳定后，再把浮窗根节点挂到 document.body，
    // 脱离 #app 的 transform（首页 1366px 屏动态设置 scale(0.84)），使其内部
    // position: fixed 恢复为相对视口定位。
    // 用 setTimeout 延迟 append，避开 Vue 初次挂载未稳定的窗口期，避免因过早移出 $el
    // 导致点击事件失效。同时清理历史/预渲染残留的“非自身” root，保证页面只有一个浮窗。
    this._appendTimer = setTimeout(() => {
      if (!this.$el) return;
      // app.vue 在路由切换后异步设置 viewport meta，此刻再确认一次缩放系数
      this.syncPageScale();
      document.querySelectorAll('.search-float-root').forEach((el) => {
        if (el && el !== this.$el) {
          try {
            el.remove();
          } catch (e) {
            el.parentNode && el.parentNode.removeChild(el);
          }
        }
      });
      if (this.$el.parentNode !== document.body) {
        document.body.appendChild(this.$el);
      }
      // 挂载完成后再触发浮窗入场动画（从右下角滑入到位）
      this.$nextTick(() => {
        this.btnLoaded = true;
      });
    }, 1000);

    // iOS 软键盘弹出/收起带动 visualViewport 尺寸变化，实时刷新可视高度，
    // 由 dialogStyle 重新收缩/定位弹窗，保证整窗始终落在键盘上方。
    if (this.isMobile && window.visualViewport) {
      this._onVvResize = () => {
        if (this.suggestionInputFocused) {
          this.updateKbOffset();
        } else {
          this.kbVisibleH = 0;
          this.kbVisTop = 0;
        }
      };
      window.visualViewport.addEventListener("resize", this._onVvResize);
      window.visualViewport.addEventListener("scroll", this._onVvResize);
    }
  },
  beforeDestroy() {
    if (this._appendTimer) {
      clearTimeout(this._appendTimer);
    }
    if (this.suggestionDebounceTimer) {
      clearTimeout(this.suggestionDebounceTimer);
    }
    if (this._kbTimer) {
      clearInterval(this._kbTimer);
      this._kbTimer = null;
    }
    if (this.suggestionController) {
      this.suggestionController.abort();
    }
    if (this._wheelBlocker && this.$el) {
      this.$el.removeEventListener("wheel", this._wheelBlocker);
      this.$el.removeEventListener("touchmove", this._wheelBlocker);
    }
    if (this._onVvResize && window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this._onVvResize);
      window.visualViewport.removeEventListener("scroll", this._onVvResize);
      this._onVvResize = null;
    }
    this.endResize();
    if (this.$el && this.$el.parentNode && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el);
    }
  },

  methods: {
    // —— 右下角拖拽拉伸 ——
    // —— 顶部拖拽拉伸（仅高度，锚定底边不动） ——
    startResizeTop(e) {
      this.resizing = true;
      // 顶边 handle：向上拖缩小、向下拖增大，高度由「移动前的起始高度 - 纵向位移」得出
      this._rsTop = { y: e.clientY, h: this.dialogH };
      document.body.style.cursor = "ns-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", this.onResizeTop);
      document.addEventListener("mouseup", this.onResizeEnd);
    },
    onResizeTop(e) {
      if (!this.resizing || !this._rsTop) return;
      const maxH = window.innerHeight - 48;
      const h = Math.min(Math.max(this._rsTop.h + (this._rsTop.y - e.clientY), this.dialogMinH), maxH);
      // 只调整高度；弹窗以 right/bottom 锚定，改高度后顶边自动上下移动、底边不动
      this.dialogH = h;
    },
    // —— 左侧拖拽拉伸（仅宽度，锚定右边缘不动） ——
    startResizeLeft(e) {
      this.resizing = true;
      // 左边缘 handle：向右拖增大、向左拖缩小，宽度由「起始宽度 + 向左位移」得出
      this._rsLeft = { x: e.clientX, w: this.dialogW };
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", this.onResizeLeft);
      document.addEventListener("mouseup", this.onResizeEnd);
    },
    onResizeLeft(e) {
      if (!this.resizing || !this._rsLeft) return;
      const maxW = window.innerWidth - 48;
      const w = Math.min(Math.max(this._rsLeft.w + (this._rsLeft.x - e.clientX), this.dialogMinW), maxW);
      // 只调整宽度；弹窗以 right/bottom 锚定，改宽度后左边缘随鼠标水平移动、右边缘不动
      this.dialogW = w;
    },
    startResize(e) {
      this.resizing = true;
      this._rs = { x: e.clientX, y: e.clientY, w: this.dialogW, h: this.dialogH };
      document.body.style.cursor = "nwse-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", this.onResize);
      document.addEventListener("mouseup", this.onResizeEnd);
    },
    onResize(e) {
      if (!this.resizing || !this._rs) return;
      const maxW = window.innerWidth - 48; // 视口留边
      const maxH = window.innerHeight - 48;
      // 手柄在左上角、弹窗锚定右下：向右下拖变小，向左上拖变大
      const w = Math.min(Math.max(this._rs.w - (e.clientX - this._rs.x), this.dialogMinW), maxW);
      const h = Math.min(Math.max(this._rs.h - (e.clientY - this._rs.y), this.dialogMinH), maxH);
      this.dialogW = w;
      this.dialogH = h;
    },
    onResizeEnd() {
      this.resizing = false;
      this._rs = null;
      this._rsTop = null;
      this._rsLeft = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", this.onResize);
      document.removeEventListener("mousemove", this.onResizeTop);
      document.removeEventListener("mousemove", this.onResizeLeft);
      document.removeEventListener("mouseup", this.onResizeEnd);
    },
    endResize() {
      if (!this.resizing) return;
      this.onResizeEnd();
    },
    openWidget() {
      this.open = true;
      this.$nextTick(() => this.scrollToBottom());
    },
    closeWidget() {
      this.open = false;
      this.closeSuggestions();
    },
    // —— 聊天 ——
    clearChat() {
      // 清理所有打字机定时器
      (this.messages || []).forEach((m) => {
        if (m.typeTimer) {
          clearInterval(m.typeTimer);
          m.typeTimer = null;
        }
      });
      this.searchSeq++; // 使在途请求结果作废
      this.isSearching = false; // 立即恢复发送按钮
      this.messages = [];
      this.showScrollDown = false; // 清空后无内容，箭头一并隐藏
      this.keyword = "";
      this.closeSuggestions();
    },
    quickSearch(word) {
      this.keyword = word;
      this.sendMessage();
    },
    async sendMessage() {
      const question = (this.keyword || "").trim();
      if (!question || this.isSearching) {
        return;
      }
      // 点击发送 / 回车后主动收起软键盘
      if (this.isMobile) {
        this.dismissKeyboard();
      }
      this.keyword = "";
      this.closeSuggestions();
      // 追加用户消息 + AI 占位
      this.messages.push({ role: "user", text: question });
      const aiMsg = { role: "ai", text: "", loading: true, error: false, displayedHtml: "", references: [], keyword: question };
      this.messages.push(aiMsg);
      const seq = ++this.searchSeq;
      this.isSearching = true;
      // 发送后立刻平滑滚动到最新消息（用户消息 + AI loading 占位已在底部），
      // 不等后台响应返回后再滚动，避免用户以为没有回复
      this.scrollToBottomSmooth();
      try {
        const res = await publicSearch(question);
        if (seq !== this.searchSeq) {
          return; // 会话已被清空，作废该结果
        }
        this.isSearching = false;
        aiMsg.loading = false;
        if (!res || res.code !== "OK") {
          aiMsg.error = true;
          aiMsg.errorCode = (res && res.code) || "NETWORK_ERROR";
          aiMsg.text = (res && ERROR_MESSAGES[res.code]) || (res && res.message) || "搜索失败，请稍后重试。";
          return;
        }
        const data = res.data || {};
        // 新版接口按 referenceGroups 分组返回（含 primary/sourceKey/source/references）；
        // 兼容旧版扁平 references，无分组时包装成单个 primary 分组。
        const groups =
          data.referenceGroups && data.referenceGroups.length
            ? data.referenceGroups
            : data.references && data.references.length
            ? [{ sourceKey: "zoneyucn", source: "集团官网", primary: true, references: data.references }]
            : [];
        aiMsg.referenceGroups = groups;
        aiMsg.references = data.references || [];
        aiMsg.keyword = question;
        this.typewrite(aiMsg, data.answer || "");
      } catch (e) {
        if (seq !== this.searchSeq) {
          return; // 会话已被清空，忽略失败结果
        }
        this.isSearching = false;
        aiMsg.loading = false;
        aiMsg.error = true;
        aiMsg.errorCode = "NETWORK_ERROR";
        aiMsg.text = "网络开小差啦！请重新发送。";
      }
    },
    handleSearch() {
      // 回车且联想被高亮时，优先选中联想
      if (this.showSuggestions && this.activeSuggestionIndex >= 0 && this.suggestions[this.activeSuggestionIndex]) {
        this.selectSuggestion(this.suggestions[this.activeSuggestionIndex]);
        return;
      }
      this.sendMessage();
    },
    // 输入框回车：中文输入法（IME）组合中按下回车用于“选中文”，不应发送。
    // 浏览器通过 isComposing 标识组合态；WebKit/Safari 内核组合态下 keyCode 常为 229。
    // 二者任一命中即忽略，避免 iOS/偶发 Safari 下拼音选词后文字被直接发送出去。
    onEnterKey(e) {
      if ((e.isComposing !== undefined && e.isComposing) || e.keyCode === 229) {
        return;
      }
      this.handleSearch();
    },
    // —— 打字机 ——
    typewrite(msg, text) {
      // 每个新问答开始时重置“用户已滚动”标记：本次回答用户不滚则跟随，滚过则停止
      this._userMovedScroll = false;
      msg.fullText = text;
      msg.displayedHtml = "";
      msg.typeIndex = 0;
      msg.typeTimer = setInterval(() => {
        const t = msg.fullText || "";
        if (msg.typeIndex < t.length) {
          msg.typeIndex++;
          const shown = t.slice(0, msg.typeIndex);
          msg.displayedHtml = this.renderAnswer(shown);
          // PC 与手机端统一：逐行定位精细滚动，滚到当前正在打印的文字行
          // （用户已手动滚动则暂停跟随，由 _userMovedScroll 控制）
          this.scrollToPrintingLine();
          // 每次内容变化后用 nextTick 同步刷新箭头：打字中内容溢出、滚动条出现，
          // 或停留底部附近时，都能即时更新“滚动到底”箭头是否展示
          this.$nextTick(() => this.onBodyScroll());
        } else {
          clearInterval(msg.typeTimer);
          msg.typeTimer = null;
          if (this.isMobile) {
            this.scrollToBottomIfNear();
          }
        }
      }, 18);
    },
    renderAnswer(text) {
      if (!text) return "";
      const safe = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br/>");
      return safe;
    },
    // —— 联想 ——
    onInputFocus() {
      this.suggestionInputFocused = true;
      this.maybeShowSuggestions();
      // 手机端键盘弹出：记录聚焦态并开始跟随可视视口变化。
      // 定位完全由 dialogStyle 依据「可视高度」计算（与弹窗当前位置无关），
      // 因此可以随键盘展开动画反复重算而不会来回振荡，无需锁存/反馈校正。
      if (this.isMobile) {
        this.kbActive = true;
        this.updateKbOffset();
        this.startKbPolling();
      }
    },
    onInputBlur() {
      // 停止键盘轮询
      if (this._kbTimer) {
        clearInterval(this._kbTimer);
        this._kbTimer = null;
      }
      setTimeout(() => {
        this.suggestionInputFocused = false;
        this.closeSuggestions();
        // 键盘收起：复位为普通居中态
        this.kbActive = false;
        this.kbVisibleH = 0;
        this.kbVisTop = 0;
      }, 150);
    },
    // 定时轮询：键盘展开动画期间可视高度会连续变化，轮询持续刷新，使弹窗平滑跟随。
    // （定位公式与弹窗当前位置无关，只依赖可视高度，故持续重算也不会振荡。）
    startKbPolling() {
      if (this._kbTimer) return;
      this._kbTimer = setInterval(() => {
        if (!this.suggestionInputFocused) return;
        this.updateKbOffset();
      }, 80);
    },
    // 手机端：读取软键盘弹出后的「可视区高度 / 顶部偏移」（均为布局 px），供 dialogStyle 使用。
    // 兼容三层：
    //  1) visualViewport 存在（iOS/Chrome）：直接取 vv.height / vv.offsetTop，精确；
    //  2) 有 innerHeight 收缩（普通安卓 WebView，键盘挤压布局视口）：取收缩后的 innerHeight，
    //     此时布局视口同步变小，弹窗会自然缩小居中（dialogStyle 中 kbUp 为 false）；
    //  3) 百度等国产内核既无 visualViewport、innerHeight 也全屏不变（键盘“覆盖”式弹出）：
    //     无法量测键盘高度，按「键盘约占屏高 38%」估算可视高度，保证整窗落在键盘上方。
    updateKbOffset() {
      const vv = window.visualViewport;
      if (vv) {
        this.kbVisTop = vv.offsetTop || 0;
        this.kbVisibleH = vv.height;
      } else {
        const layoutH = document.documentElement.clientHeight || window.innerHeight || 0;
        const vh = window.innerHeight || 0;
        this.kbVisTop = 0;
        this.kbVisibleH = vh > 0 && vh < layoutH - 1
          ? vh                              // 键盘挤压了布局视口：可视高度即收缩后的 innerHeight
          : Math.round(layoutH * 0.62);     // 覆盖式键盘：按经验估算可视高度
      }
      // 实测净缩放：渲染尺寸 / style 尺寸。宽度不受键盘收缩影响，故只需测一次即可（>0 视为已测）。
      if (this.mobileNetScale > 0) return;
      const dialogEl = this.$el && this.$el.querySelector(".float-dialog");
      if (!dialogEl) return;
      const cssW = parseFloat(dialogEl.style.width) || 0;
      const visW = dialogEl.getBoundingClientRect().width;
      if (cssW > 0 && visW > 0) this.mobileNetScale = visW / cssW;
    },
    // 发送后主动收起键盘（输入框失焦）
    dismissKeyboard() {
      const input = this.$refs.chatInput;
      if (input && input.blur) {
        input.blur();
      }
    },
    // iOS Safari 缺陷（仅 .is-ios 的输入框，其上有 transform: scale(3)）：
    // 输入框内容被清空后，占位文案不会被重新排版，仍沿用“上一次有内容时”的排版/滚动状态，
    // 于是「打几个字 → 发送 → 清空」之后，占位文案「请输入问题，按 Enter 发送...」看起来只剩下几个字。
    // 这里在内容清空时复位滚动位置，并重新挂一次 placeholder 属性迫使 iOS 按当前空内容重新测量绘制。
    iosResetPlaceholder() {
      if (!this.isIos) return;
      this.$nextTick(() => {
        const el = this.$refs.chatInput;
        if (!el) return;
        // 1) 显式清空 DOM 值：某些 iOS 版本对“经 v-model 清空”的 textarea 不会立即重绘
        if (el.value) el.value = "";
        // 2) 复位滚动：避免清空后残留的横向滚动把占位文案的前半截挤出可视区
        el.scrollLeft = 0;
        el.scrollTop = 0;
        // 3) 重挂 placeholder：删掉属性再立即恢复，强制同步重排一次（同一帧内完成，不会闪烁）
        const ph = el.getAttribute("placeholder") || "";
        el.setAttribute("placeholder", "");
        if (ph) void el.offsetWidth; // 读取布局属性，强制同步 reflow
        el.setAttribute("placeholder", ph);
        // 4) 微调一次字距再还原：让 iOS 重新测量文本排版（占位文案的裁剪宽度就来自上一次文本测量）
        const prevSpacing = el.style.letterSpacing;
        el.style.letterSpacing = "0.01px";
        void el.offsetWidth; // 强制同步 reflow
        el.style.letterSpacing = prevSpacing;
        el.scrollLeft = 0;
      });
    },
    onInput() {
      this.activeSuggestionIndex = -1;
      if (this.suggestionDebounceTimer) {
        clearTimeout(this.suggestionDebounceTimer);
      }
      this.suggestionDebounceTimer = setTimeout(() => {
        this.loadSuggestions();
      }, 250);
    },
    maybeShowSuggestions() {
      if (this.suggestionInputFocused && this.suggestions.length > 0 && this.keyword.trim()) {
        this.showSuggestions = true;
      }
    },
    closeSuggestions() {
      this.showSuggestions = false;
      this.activeSuggestionIndex = -1;
    },
    async loadSuggestions() {
      const q = (this.keyword || "").trim();
      if (!q) {
        this.closeSuggestions();
        return;
      }
      if (this.suggestionController) {
        this.suggestionController.abort();
      }
      const controller = new AbortController();
      this.suggestionController = controller;
      const requestId = ++this.suggestionRequestId;
      try {
        const res = await publicSearchSuggestions(q, 5, controller.signal);
        if (requestId !== this.suggestionRequestId || controller.signal.aborted) {
          return;
        }
        const list = (res && res.data && res.data.suggestions) || [];
        this.suggestions = list;
        this.activeSuggestionIndex = -1;
        this.showSuggestions = this.suggestionInputFocused && list.length > 0 && !!this.keyword.trim();
      } catch (e) {
        if (requestId === this.suggestionRequestId && !controller.signal.aborted) {
          this.closeSuggestions();
        }
      }
    },
    onSuggestionKeydown(step) {
      if (!this.showSuggestions || this.suggestions.length === 0) {
        return;
      }
      const len = this.suggestions.length;
      this.activeSuggestionIndex = (this.activeSuggestionIndex + step + len) % len;
    },
    selectSuggestion(sug) {
      if (!sug || !sug.question) {
        return;
      }
      this.keyword = sug.question;
      this.closeSuggestions();
      this.sendMessage();
    },
    // —— 工具 ——
    highlightKeyword(text, keyword) {
      if (!text || !keyword) return text || "";
      let safe = String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const reg = new RegExp(`(${this.escapeRegExp(keyword)})`, "gi");
      return safe.replace(reg, '<span class="hl">$1</span>');
    },
    escapeRegExp(string) {
      return String(string).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },
    openRef(url) {
      if (!url) return;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    // 统计分组参考资料总条数
    refCount(groups) {
      return (groups || []).reduce((sum, g) => sum + (g.references || []).length, 0);
    },
    // 从 viewport meta 中解析整页缩放系数。兼容两种写法：
    //  1) 显式 initial-scale=0.234375, ...（当前写法，需用正则提取）
    //  2) 早期遗留的纯数字 "0.234375"（parseFloat 可直接读取）
    readViewportScale() {
      const meta = document.querySelector('meta[name="viewport"]');
      if (!meta) return 0;
      const content = meta.getAttribute("content") || "";
      const m = /initial-scale\s*=\s*([0-9.]+)/i.exec(content);
      if (m) {
        const v = parseFloat(m[1]);
        return isFinite(v) && v > 0 ? v : 0;
      }
      const c = parseFloat(content);
      return isFinite(c) && c > 0 ? c : 0;
    },
    // 读取页面整体缩放系数（viewport meta 的 content 值即 screen.width/1600），
    // 手机端用于对浮窗尺寸做反向补偿，使 UI 呈现为合理的物理尺寸。
    syncPageScale() {
      const c = this.readViewportScale();
      if (c > 0) {
        this.pageScale = c;
        return true;
      }
      return false;
    },
    // 阻止弹窗内滚轮/触摸滚动穿透到 #app 页面：
    // - 命中可滚动的消息区时，交给消息区自己滚动（配合 overscroll-behavior:contain 防链式传出）
    // - 否则（消息区不可滚动 / 事件在头部、输入区、空白区）阻断默认滚动，避免滚动背景页面
    onWheelBlocker(e) {
      if (!this.open) return;
      const body = this.$refs.bodyRef;
      const bodyScrollable = body ? body.scrollHeight > body.clientHeight + 1 : false;
      const targetInBody = body ? body.contains(e.target) : false;
      if (targetInBody && bodyScrollable) {
        return; // 由消息区处理滚动，overscroll-behavior:contain 已禁止链式传出
      }
      e.preventDefault();
      e.stopPropagation();
    },
    scrollToBottom() {
      this._lastAuto = Date.now(); // 标记为程序滚动，避免误判成用户滚动
      this.$nextTick(() => {
        const el = this.$refs.bodyRef;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
      this.showScrollDown = false;
    },
    // 平滑滚动到底部（点击右下角箭头时使用，慢慢滑到底）
    scrollToBottomSmooth() {
      this._lastAuto = Date.now();
      this.$nextTick(() => {
        const el = this.$refs.bodyRef;
        if (el) {
          el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        }
      });
      this.showScrollDown = false;
    },
    // 消息区滚动时更新右下角箭头显隐：不在底部则显示
    onBodyScroll() {
      const el = this.$refs.bodyRef;
      if (!el) return;
      this.showScrollDown = el.scrollHeight - el.scrollTop - el.clientHeight > 24;
    },
    // 消息区是否已到接近底部（容差值 px）
    isNearBottom(tolerance = 80) {
      const el = this.$refs.bodyRef;
      if (!el) return true;
      return el.scrollHeight - el.scrollTop - el.clientHeight < tolerance;
    },
    // 接近底部时才跟随滚动到底，避免用户上滑时被打字机强制拉回造成抖动
    scrollToBottomIfNear() {
      if (this.isNearBottom()) {
        this.scrollToBottom();
      }
    },
    // 逐行定位精细滚动：让「正在打印的 AI 气泡」底端贴近可视区底部。
    // 关键约束：始终保证气泡第一行可见。当继续往下滚会即将把第一行顶出可视区时，
    // 停止滚动（宁可放弃跟随最后一行，也要保住第一行可用性）。
    scrollToPrintingLine() {
      // 用户已手动滚动过滚动条：停止跟随打字机，交给用户自由浏览
      if (this._userMovedScroll) return;
      this.$nextTick(() => {
        const container = this.$refs.bodyRef;
        if (!container) return;
        const ai = container.querySelector(".chat-row.row-ai:last-child .ai-answer");
        if (!ai) {
          this.scrollToBottom();
          return;
        }
        const crect = container.getBoundingClientRect();
        const arect = ai.getBoundingClientRect();
        // 打印行超出可视区底部所需滚动量（底部留白 gap，目标贴底）
        const gap = 12;
        const deltaBottom = arect.bottom - crect.bottom + gap;
        if (deltaBottom <= 0) return; // 打印行仍可见，无需滚动
        // 第一行距离可视区顶的剩余可用空间（topPad 为希望保留的顶部间距）
        const topPad = 8;
        const availableBeforeHide = arect.top - crect.top - topPad;
        // 最多只能滚到「第一行仍可见」：若当前空间不足，则只滚到可用空间为止
        const scrollBy = Math.min(deltaBottom, Math.max(availableBeforeHide, 0));
        if (scrollBy > 0) {
          this._lastAuto = Date.now(); // 程序滚动打点，勿误判为用户滚动
          container.scrollTop += scrollBy;
          // scroll 事件异步派发，直接同步刷新箭头显隐，避免“滚动条动一下才有箭头”
          this.onBodyScroll();
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
// 根节点：仅作包装，不改动定位，由内部按钮/弹窗用 position: fixed 相对视口定位
// （与顶部 header 的 fixed 定位方式一致；不参与 fixed/absolute 包含块判断）。
.search-float-root {
  position: fixed;
  z-index: 999;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
}

// 包裹层：固定到视口左上 0x0，仅容纳两个 fixed 定位按钮，不参与包含块
.btn-switch {
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  z-index: 999;
}

// 悬浮按钮 —— fixed 相对视口（组件已 append 到 body，不受 #app 的 transform 缩放影响），
// 使用 ai.png 图片，持续上下浮动 2px（无背景、光晕、hover）。
// PC：外层 .float-holder 承载“从右下角滑入”的入场动画，内层 .float-btn 做上下浮动 + 名牌。
.float-holder {
  position: fixed;
  right: 40px;
  bottom: 70px;
  opacity: 0;
  transform: translate(60px, 60px);
  transition: opacity 0.5s ease-out, transform 0.5s ease-out;
  pointer-events: none;

  // 页面加载完成后由 .btn-switch.loaded 触发入场；滑入到位的浮动交给 .float-btn
}

.btn-switch.loaded .float-holder {
  opacity: 1;
  transform: translate(0, 0);
  pointer-events: auto;
}

.float-btn {
  position: relative;
  width: 80px;
  height: 80px;
  border: none;
  border-radius: 12px;
  background: url("../assets/ai.png") no-repeat center / contain;
  cursor: pointer;
  animation: floatUpDown 1.2s ease-in-out infinite;

  // 底部名牌“中峪AI助手”（AI 机器人举牌子）
  .float-tag {
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    white-space: nowrap;
    padding: 3px 10px;
    font-size: 12px;
    line-height: 1;
    color: #fff;
    background: linear-gradient(100deg, #e82814 0%, #a90100 100%);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.85);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
    pointer-events: none;
  }
}

.float-mask {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.45);
  z-index: 997;
}

// 移动端 AI 机器人图标：仅手机时由 v-else 渲染。位置与大小使用页面统一的
// 1600 设计坐标（viewport meta 会整体等比缩放到设备），从而与页面内容同比例。
// 浮动动画放在内部子元素 .float-btn-pulse 上。
// 入场：借助 .btn-switch.loaded 做右下角滑入过渡（transform 受 meta 缩放影响较小，采用 translate 百分比避免换算）。
.float-btn-mobile {
  position: fixed;
  right: 40px;
  bottom: 50px;
  width: 80px;
  height: 80px;
  cursor: pointer;
  opacity: 0;
  transform: translate(40px, 60px);
  transition: opacity 0.5s ease-out, transform 0.5s ease-out;

  .btn-switch.loaded & {
    opacity: 1;
    transform: translate(0, 0);
  }

  .float-btn-pulse {
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 12px;
    background: url("../assets/ai.png") no-repeat center / contain;
    animation: floatUpDown 1.2s ease-in-out infinite;
  }

  // 底部名牌“中峪AI助手”（紧贴机器人底边，具体间距由 JS mobileTagStyle 按 pageScale 补偿）
  .float-tag-mobile {
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    white-space: nowrap;
    padding: 2px 6px;
    font-size: 9px;
    line-height: 1.1;
    color: #fff;
    background: linear-gradient(100deg, #e82814 0%, #a90100 100%);
    border-radius: 5px;
    border: 1px solid rgba(255, 255, 255, 0.85);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
    pointer-events: none;
  }
}

// 手机端（按 UA 判断 isMobile）：JS computed 已算出基准 size；
// 定位用 left/top 50% + translate(-50%,-50%) 使弹窗水平垂直居中。
.float-dialog.is-mobile {
  left: 50% !important;
  right: auto !important;
  top: 50% !important;
  bottom: auto !important;
  // 安卓方案 B：以 9 倍像素密度渲染，配合 JS 侧 scale(1/3) 向下缩小，
  // 净视觉仍为原 3 倍放大但更高清。iOS 上 zoom 行为异常（字体不放大而框放大），
  // 已通过 :is-ios 类移除 zoom，iOS 交由 JS 侧整体 scale(3) 等比放大。
  // 圆角：会被净缩放放大（安卓 zoom×scale1/3），视觉约 30px
  // zoom 取 F×9 = 10.35（F = JS 侧“字体放大一号”系数 1.15）；JS 侧容器宽度同步 ÷F，
  // 使弹窗外框视觉尺寸不变，只有内部文字/内边距整体放大约 15%。
  zoom: 10.35;
  border-radius: 10px;
  box-shadow: none;
  z-index: 998;
  max-width: none !important;
  max-height: none !important;
  min-width: 0 !important;
  min-height: 0 !important;

  // 移动端聚焦输入框时防止浏览器自动放大页面：
  // iOS Safari / 国产 WebView 对 font-size < 16px 的控件聚焦会缩放页面，
  // 强制 ≥16px 即可避免。iOS 隔离块（.is-ios .chat-input 42px）优先级更高，不受影响。
  .chat-input {
    font-size: 16px;
  }

  .resize-handle {
    display: none;
  }

  // 快捷问题标签：固定“上三下三”两行（共 6 个）。
  // 列宽取 auto（按内容宽度）而非 1fr：1fr 会把三列撑满弹窗宽度，导致 4 字标签之间
  // 被拉出很大空隙、看起来松散；auto + justify-content:center 让整组标签居中聚拢。
  // gap 取值比默认更紧（安卓 10→6，iOS 在 .is-ios 下 90→54），整体更紧凑。
  .welcome .quick-list {
    display: grid;
    grid-template-columns: repeat(3, auto);
    justify-content: center;
    gap: 6px;
  }

  // iOS 彻底方案：内部全部以 9 倍设计稿排版，配合 JS 侧容器 ×9 + scale(1/3) 缩回。
  // 以原生态 9 倍字号渲染，绕开 iOS 对 scale(3) 向上放大与 zoom 两者的发虚缺陷，
  // 文字/图片绝对清晰。安卓走 zoom 不经过此分支（保留 zoom:9）。
  &.is-ios {
    zoom: 1;
    // 容器按 ×9 设计稿排版 + scale(1/3) 缩回，圆角同样被缩小；
    // 取 90px 使视觉圆角与安卓一致（10px × 净3倍 = 30px 视觉）。
    border-radius: 90px;

    // header 头部（14/16/34/12/6 → 126/144/306/108/54）
    .dialog-header {
      padding: 126px 144px;
      .dialog-title {
        font-size: 144px;
        .dialog-dot {
          width: 306px;
          height: 306px;
          margin-right: 108px;
          border-radius: 54px;
        }
      }
      .dialog-actions {
        gap: 72px;
        .mini-btn {
          font-size: 108px;
          padding: 27px 90px;
          border-radius: 108px;
        }
        .close-btn {
          width: 288px;
          height: 288px;
          border-radius: 50%;
          svg {
            // 模板里是内联 16px，需覆盖放大到设计稿等价尺寸
            width: 162px !important;
            height: 162px !important;
          }
        }
      }
    }

    // 消息区（20/16 → 180/144）
    .dialog-body {
      padding: 180px 144px;
    }

    // 空状态  welcome（40/18/10/13/28/18 → 360/162/90/117/252/162）
    .welcome {
      padding-top: 360px;
      .welcome-title {
        font-size: 162px;
        margin-bottom: 90px;
      }
      .welcome-sub {
        font-size: 117px;
        margin-bottom: 252px;
        line-height: 1.6;
      }
      .quick-list {
        gap: 54px;
        .quick-item {
          padding: 72px 144px;
          font-size: 117px;
          border-radius: 162px;
        }
      }
    }

    // 对话行  chat-row / bubble（16/32/12/10/12/16/14/4 → 144/288/108/90/108/144/126/36）
    .chat-row {
      margin-bottom: 144px;
      .chat-avatar {
        width: 288px;
        height: 288px;
        font-size: 108px;
        margin-right: 90px;
        border-radius: 50%;
      }
      .avatar-user {
        margin-left: 90px;
        margin-right: 0;
      }
      .chat-bubble {
        max-width: 76%;
        padding: 108px 144px;
        border-radius: 126px;
        font-size: 126px;
        line-height: 1.7;
      }
      .bubble-ai {
        border-top-left-radius: 36px;
      }
      .bubble-user {
        border-top-right-radius: 36px;
      }
      .loading-row {
        gap: 36px;
        .dot {
          width: 63px;
          height: 63px;
          border-radius: 50%;
        }
        .loading-text {
          margin-left: 72px;
          font-size: 117px;
        }
      }
      // 参考资料  refs（12/10/12/8/8/8/8/72/6/10/4/13/11/12 → ×9）
      .refs {
        margin-top: 108px;
        padding-top: 90px;
        .refs-title {
          font-size: 108px;
          margin-bottom: 72px;
        }
        .refs-group-title {
          font-size: 108px;
          font-weight: 600;
          margin: 90px 0 54px;
        }
        .ref-item {
          padding: 72px;
          margin-bottom: 72px;
          border-radius: 72px;
          .ref-cover {
            /* iOS 右侧缩放到基础样式的 2/3，仍保持 7:4 横图比例
               (140×80 → 1260×720 → 缩小 2/3 → 840×480)。
               显式声明 cover + 深色背景，确保与 PC/安卓一致 */
            width: 840px;
            height: 480px;
            object-fit: cover;
            border-radius: 54px;
            margin-right: 90px;
            background: #e2dcd0;
          }
          .ref-main {
            gap: 36px;
          }
          .ref-title {
            font-size: 117px;
          }
          .ref-src {
            font-size: 99px;
            padding: 9px 72px;
            border-radius: 36px;
          }
          .ref-summary {
            font-size: 108px;
            line-height: 1.5;
          }
        }
      }
    }

    // 联想下拉框  suggest（10/6/10/14/16/10/14 → 90/54/90/126/144/90/126）
    .suggest-box {
      bottom: calc(100% + 36px);
      border-radius: 90px;
      padding: 54px 0;
      .suggest-item {
        padding: 90px 126px;
        .suggest-icon {
          width: 144px;
          height: 144px;
          margin-right: 90px;
        }
        .suggest-text {
          font-size: 126px;
        }
      }
    }

    // 输入区  footer（12/16/14/96/6/40/12/16/2 → 108/144/126/864/54/360/108/144/18）
    .dialog-footer {
      padding: 108px 144px;
      // iOS 隔离输入框：父容器 scale(1/3) 会让 iOS Safari 的光标（caret）渲染异常消失。
      // 用 scale(3) 抵消父缩放，使输入框净缩放 = 1，光标以原生 1:1 渲染正常显示。
      // 字号由 126px 改为 42px（42 × 净1 = 42px 视觉，与整体净 3 倍一致）。
      .chat-input {
        font-size: 42px;
        line-height: 1.6;
        caret-color: #941c24;
        transform: scale(3);
        transform-origin: left center;
        max-height: 864px;
        padding: 54px 0;
      }
      .send-btn {
        // 输入框 scale(3) 会创建堆叠上下文并覆盖本按钮（无 transform 时渲染层级更低），
        // 提升到其上，避免 iOS 上点击发送被缩放后的输入框拦截
        position: relative;
        z-index: 5;
        width: 432px;
        height: 432px;
        border-radius: 50%;
        margin-left: 108px;
        svg {
          // 模板里是内联 20px，需覆盖放大到设计稿等价尺寸
          width: 216px !important;
          height: 216px !important;
        }
        .send-loading {
          width: 144px;
          height: 144px;
          border-width: 18px;
          border-radius: 50%;
        }
      }
    }
  }
}

@keyframes floatUpDown {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

// dialog-dot 上下浮动（幅度 3px）
@keyframes floatUpDownDot {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

// 弹窗 —— 宽高由 :style 动态控制（支持拖拽拉伸），这里只用相对视口定位与最小/最大约束
.float-dialog {
  position: fixed;
  right: 24px;
  bottom: 24px;
  min-width: 420px;
  max-width: calc(100vw - 48px);
  min-height: 400px;
  max-height: calc(100vh - 48px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 16px 60px rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: dialogIn 0.22s ease-out;
}

@keyframes dialogIn {
  from { opacity: 0; transform: translateY(16px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

// 左上角拉伸把手（透明不可见，仅作为拖拽热区）
.resize-handle {
  position: absolute;
  left: 0;
  top: 0;
  width: 22px;
  height: 22px;
  cursor: nwse-resize;
  z-index: 30;
  opacity: 0;
}

// 顶部上下拉伸把手（透明细长条，仅调整高度）：跨弹窗顶部宽度，向下拖增大、向上拖缩小
.resize-handle-top {
  position: absolute;
  left: 12px;
  right: 12px;
  top: 0;
  height: 8px;
  cursor: ns-resize;
  z-index: 40;
  opacity: 0;
}

// 左侧左右拉伸把手（透明细长条，仅调整宽度）：贴弹窗左边缘，向右拖增大、向左拖缩小
.resize-handle-left {
  position: absolute;
  left: 0;
  top: 12px;
  bottom: 12px;
  width: 8px;
  cursor: ew-resize;
  z-index: 40;
  opacity: 0;
}

// 头部：红渐变，从接近 #a90100 的深红渐亮到橙红，与 #a90100 明显区分
.dialog-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: linear-gradient(100deg, #e82814 0%, #cf150c 55%, #a90100 100%);

  .dialog-title {
    display: flex;
    align-items: center;
    color: #fff;
    font-size: 16px;
    font-weight: 600;

    .dialog-dot {
      width: 34px;
      height: 34px;
      border-radius: 6px;
      background: url("../assets/ai.png") no-repeat center / contain;
      margin-right: 12px;
      flex-shrink: 0;
      animation: floatUpDownDot 1.2s ease-in-out infinite;
    }
  }

  .dialog-actions {
    display: flex;
    align-items: center;
    gap: 8px;

    .mini-btn {
      // 头部是红色渐变底，半透明白边框会与红底混成一片、几乎看不清，改用不透明白
      border: 1px solid #fff;
      background: transparent;
      color: #fff;
      font-size: 12px;
      padding: 3px 10px;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: rgba(255, 255, 255, 0.2);
      }
    }

    .close-btn {
      border: none;
      background: transparent;
      color: #fff;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      transition: background 0.2s;

      &:hover {
        background: rgba(255, 255, 255, 0.2);
      }
    }
  }
}

// 消息区
.dialog-body {
  position: relative;
  flex: 1;
  overflow-y: auto;
  /* 阻止滚动链式传递到外层页面（#app），避免滚动到底/顶时带动背景 */
  overscroll-behavior: contain;
  padding: 20px 16px;
  background: #f7f4ef;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(148, 28, 36, 0.3);
    border-radius: 3px;
  }
}

// 右下角“滚动到底部”向下箭头。相对整窗 .float-dialog（fixed）绝对定位，
// 放在 footer 上方、可视区右下角；不随 .dialog-body 滚动，隐藏也不影响滚动布局。
.scroll-down-btn {
  position: absolute;
  right: 20px;
  bottom: 92px; /* 避开 footer，位于消息区右下角 */
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #fff; /* 白底 */
  color: #a90100; /* 红色箭头 */
  border: 1px solid rgba(169, 1, 0, 0.3);
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: transform 0.15s, box-shadow 0.15s;
  z-index: 30;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
}

// 箭头淡入淡出
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// 空状态
.welcome {
  text-align: center;
  padding-top: 40px;

  .welcome-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;
  }

  .welcome-sub {
    font-size: 13px;
    color: #999;
    margin-bottom: 28px;
    line-height: 1.6;
  }

  .quick-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: center;

    .quick-item {
      padding: 8px 16px;
      font-size: 13px;
      color: #941c24;
      background: #fff;
      border: 1px solid rgba(148, 28, 36, 0.2);
      border-radius: 18px;
      cursor: pointer;
      transition: all 0.2s;
      // 触屏设备默认高亮/聚焦残留会导致选中态异常，禁用 tap 高亮与聚焦轮廓
      -webkit-tap-highlight-color: transparent;

      &:focus,
      &:active {
        outline: none;
        background: #fff;
        color: #941c24;
      }

      // 仅在有 hover 能力的设备（PC 鼠标）上展示悬停高亮；触屏/手机不改变样式
      @media (hover: hover) {
        &:hover {
          background: #941c24;
          color: #fff;
        }
      }
    }
  }
}

// 对话行
.chat-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;

  &.row-ai {
    justify-content: flex-start;
  }

  &.row-user {
    justify-content: flex-end;
  }

  .chat-avatar {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: linear-gradient(135deg, #941c24 0%, #b20809 100%);
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
  }

  .avatar-user {
    background: #c8c2b8;
    margin-left: 10px;
    margin-right: 0;
  }

  .chat-bubble {
    max-width: 76%;
    padding: 12px 16px;
    border-radius: 14px;
    font-size: 14px;
    line-height: 1.7;
    word-break: break-word;
  }

  .bubble-ai {
    background: #fff;
    color: #4a4a4a;
    border-top-left-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    .ai-answer {
      white-space: pre-wrap;
      word-break: break-word;
    }

    .ai-error {
      color: #c51722;
    }

    // 网络/超时类错误用正常正文颜色展示，弱化"报错"观感
    .ai-error-normal {
      color: #4a4a4a;
    }
  }

  .bubble-user {
    background: linear-gradient(135deg, #941c24 0%, #b20809 100%);
    color: #fff;
    border-top-right-radius: 4px;
  }

  .loading-row {
    display: flex;
    align-items: center;
    gap: 4px;

    .dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: #b0a79b;
      animation: bounce 1.3s infinite ease-in-out both;

      &:nth-child(2) { animation-delay: -0.3s; }
      &:nth-child(3) { animation-delay: -0.15s; }
    }

    .loading-text {
      margin-left: 8px;
      font-size: 13px;
      color: #999;
    }
  }

  @keyframes bounce {
    0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
    40% { transform: scale(1); opacity: 1; }
  }

  // 参考资料
  .refs {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px dashed #ece6dd;

    .refs-title {
      font-size: 12px;
      color: #999;
      margin-bottom: 8px;
    }

    // 农业官网分组标题：primary=false 的参考资料前展示
    .refs-group-title {
      font-size: 12px;
      color: #2e7d32;
      margin: 10px 0 6px;
      font-weight: 600;
    }

    .ref-item {
      display: flex;
      align-items: center;
      padding: 8px;
      margin-bottom: 8px;
      background: #f8f5f0;
      border-radius: 8px;
      cursor: pointer;
      transition: background 0.2s;

      &:hover {
        background: rgba(148, 28, 36, 0.08);

        .ref-title {
          color: #941c24;
        }
      }

      .ref-cover {
        flex-shrink: 0;
        width: 93px;
        height: 53px;
        /* cover：保持比例等比缩放，取宽/高 100% 铺满中较大者，多余部分裁切；
           裁切后若仍有空隙（半透明图 / 未填满），以深于 .ref-item 背景(#f8f5f0) 的
           米色 #e2dcd0 铺满。
           尺寸已在手机端需求中等比缩小 2/3（140×80 → 93×53，仍为 7:4 横图比例） */
        object-fit: cover;
        border-radius: 6px;
        margin-right: 10px;
        background: #e2dcd0;
      }

      .ref-main {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .ref-title {
        font-size: 13px;
        color: #4a4a4a;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        ::v-deep .hl {
          color: #c51722;
          font-weight: 600;
        }
      }

      .ref-src {
        flex-shrink: 0;
        align-self: flex-start;
        font-size: 11px;
        color: #941c24;
        background: rgba(148, 28, 36, 0.08);
        padding: 1px 8px;
        border-radius: 4px;
      }

      .ref-summary {
        font-size: 12px;
        color: #999;
        line-height: 1.5;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    // 农业官网（zoneyucc）绿色主题：覆盖集团官网（zoneyucn）默认红色系的
    // 背景、字体、来源标签、关键词高亮，其余尺寸/iOS 覆盖均继承主样式。
    .ref-item.ref-item-agri {
      background: #f0f6ee;

      &:hover {
        background: rgba(46, 125, 50, 0.10);

        .ref-title {
          color: #2e7d32;
        }
      }

      .ref-title {
        color: #000;

        ::v-deep .hl {
          color: #2e7d32;
        }
      }

      .ref-src {
        color: #2e7d32;
        background: rgba(46, 125, 50, 0.10);
      }
    }
  }
}

// 联想下拉框（置于输入框上方，随 .dialog-footer 定位）
.suggest-box {
  position: absolute;
  bottom: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 -6px 24px rgba(0, 0, 0, 0.14);
  z-index: 30;
  padding: 6px 0;
  overflow: hidden;

  .suggest-item {
    display: flex;
    align-items: center;
    padding: 10px 14px;
    cursor: pointer;
    transition: background 0.2s;

    // 关联问题仅在 PC 鼠标悬浮时高亮；手机/触屏、键盘选中均不高亮
    @media (hover: hover) {
      &:hover {
        background: rgba(148, 28, 36, 0.06);
      }
    }

    .suggest-icon {
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      margin-right: 10px;
      color: #b0b0b0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .suggest-text {
      flex: 1;
      font-size: 14px;
      color: #4a4a4a;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      ::v-deep .hl {
        color: #c51722;
        font-weight: 600;
      }
    }
  }
}

// 输入区
.dialog-footer {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  padding: 12px 16px;
  border-top: 1px solid #f0ece5;
  background: #fff;
  position: relative;

  .chat-input {
    flex: 1;
    border: none;
    outline: none;
    resize: none;
    font-size: 14px;
    line-height: 1.6;
    color: #333;
    caret-color: #941c24;
    max-height: 96px;
    padding: 6px 0;
    background: transparent;
    font-family: inherit;
  }

  .send-btn {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 50%;
    background: linear-gradient(135deg, #941c24 0%, #b20809 100%);
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 12px;
    transition: all 0.2s;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(148, 28, 36, 0.4);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .send-loading {
      width: 16px;
      height: 16px;
      border: 2px solid rgba(255, 255, 255, 0.4);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
}
</style>
